import { Button, Card, cn } from "@heroui/react";
import { ArrowRight, Check, ClockHistory } from "react-bootstrap-icons";
import { useEffect, useRef } from "react";

import {
  recordRecommendationClick,
  recordRecommendationDisplay,
} from "@/app/helpers/user-actions";
import { Recommendation } from "@/types/dataSchema";

import { getIconFromBoostrapIconKey } from "../Options";
import {
  getEstimatedTime,
  getRecommendationReasons,
} from "../utils/recommendationUtils";

interface RecommendationCardProps {
  recommendation: Recommendation;
  index: number;
  isRevealed: boolean;
  isShiny: boolean;
  animationStarted: boolean;
  isAdditional?: boolean;
  notifiedRecommendations: Set<string>;
  contactedRecommendations: Set<string>;
  isAdmin: boolean;
  showRecommendations: boolean;
  recommendedRecommendationsKeys: any[];
  journeyData: any;
  onContactClick: (recommendation: Recommendation, action: any) => void;
  onNotifyClick: (recommendation: Recommendation, action: any) => void;
}

export function RecommendationCard({
  recommendation,
  index,
  isRevealed,
  isShiny,
  animationStarted,
  isAdditional = false,
  notifiedRecommendations,
  contactedRecommendations,
  isAdmin,
  showRecommendations,
  recommendedRecommendationsKeys,
  journeyData,
  onContactClick,
  onNotifyClick,
}: RecommendationCardProps) {
  const timeInfo = getEstimatedTime(recommendation);
  const hasRecordedDisplay = useRef(false);

  // Track recommendation display when it becomes visible
  useEffect(() => {
    if (isRevealed && !hasRecordedDisplay.current) {
      recordRecommendationDisplay({
        recommendationId: recommendation.id,
        recommendationName: recommendation.name,
      });
      hasRecordedDisplay.current = true;
    }
  }, [isRevealed, recommendation.id, recommendation.name]);

  const getCardClasses = () => {
    const baseClasses = [
      "text-lg",
      "2xl:mx-[5%] 3xl:mx-[20%] text-description-color p-10 group relative overflow-hidden",
    ];

    if (isAdditional) {
      baseClasses.push(
        "bg-gradient-to-br from-slate-700/60 to-slate-700/20 hover:bg-white/10 border border-slate-600/30",
      );
    } else {
      baseClasses.push(
        "bg-gradient-to-br from-blue-700/70 to-blue-700/20 hover:bg-white/10",
      );
    }

    // Animation classes
    if (!animationStarted) {
      baseClasses.push("opacity-0 pointer-events-none");
    } else if (!isRevealed) {
      baseClasses.push("opacity-0 blur-sm transform translate-y-8 scale-95");
    } else if (!isAdditional) {
      baseClasses.push("animate-reveal");
    } else {
      baseClasses.push(
        "opacity-100 transform translate-y-0 scale-100 transition-all duration-500 ease-out",
      );
    }

    return cn(...baseClasses);
  };

  // New single action handler
  const handleSingleAction = async (action: {
    type: "url" | "contact" | "notify";
    url?: string;
    label: string;
  }) => {
    if (action.type === "contact") {
      onContactClick(recommendation, {
        id: "primary",
        name: action.label,
        cta: true,
      });

      return;
    }

    if (action.type === "notify") {
      onNotifyClick(recommendation, {
        id: "primary",
        name: action.label,
        cta: true,
      });

      return;
    }

    // For URL actions
    if (action.url) {
      const utmUrl = await recordRecommendationClick({
        recommendationId: recommendation.id,
        recommendationName: recommendation.name,
        actionId: `${recommendation.id}_primary_action`,
        actionName: action.label,
        actionUrl: action.url,
        isCta: true,
      });

      if (utmUrl) {
        window.open(utmUrl, "_blank", "noopener,noreferrer");
      }
    }
  };

  // Get the single action for a recommendation
  const getRecommendationAction = (
    rec: Recommendation,
  ): {
    type: "url" | "contact" | "notify";
    url?: string;
    label: string;
  } | null => {
    // Priority 1: New primaryAction field
    if (rec.primaryAction) {
      const label = rec.primaryAction.ctaLabel || getDefaultCTALabel(rec.type);

      if (rec.type === "contact") return { type: "contact", label };
      if (rec.type === "notify") return { type: "notify", label };
      if (rec.primaryAction.url)
        return { type: "url", url: rec.primaryAction.url, label };
    }

    // Priority 2: Default based on type
    if (rec.type === "contact") return { type: "contact", label: "Contact Us" };
    if (rec.type === "notify") return { type: "notify", label: "Get Notified" };

    return null;
  };

  const getDefaultCTALabel = (type: string): string => {
    switch (type) {
      case "documentation":
        return "Read Documentation";
      case "course":
        return "Start Course";
      case "video":
        return "Watch Video";
      case "article":
        return "Read Article";
      case "contact":
        return "Contact Us";
      case "notify":
        return "Get Notified";
      default:
        return "Learn More";
    }
  };

  const isNotified = notifiedRecommendations.has(recommendation.id);
  const isContacted = contactedRecommendations.has(recommendation.id);
  const showConfirmation =
    (recommendation.type === "notify" && isNotified) ||
    (recommendation.type === "contact" && isContacted);

  const renderActions = () => {
    if (showConfirmation) {
      return (
        <div className="mt-4 flex items-center gap-2 text-green-600">
          <Check className="text-lg" />
          <span className="text-sm font-medium">
            {recommendation.type === "notify"
              ? "You'll be notified when available"
              : "Contact request sent"}
          </span>
        </div>
      );
    }

    // New simplified single action logic
    const action = getRecommendationAction(recommendation);

    if (!action) return null;

    return (
      <div className="text-sm">
        <Button
          className="mt-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-indigo-500/20 group-hover:scale-105 group-hover:opacity-100"
          size="md"
          onPress={() => handleSingleAction(action)}
        >
          {action.label}
          <ArrowRight className={cn("ml-2", "inline")} />
        </Button>
      </div>
    );
  };

  const renderAdminReasons = () => {
    if (!isAdmin || !showRecommendations) return null;

    const reasons = getRecommendationReasons(
      recommendation.id,
      recommendedRecommendationsKeys,
      journeyData,
    );

    if (reasons.strong.length === 0 && reasons.optional.length === 0) {
      return null;
    }

    return (
      <div className="mt-3 p-3 bg-gray-800/50 rounded-lg border border-gray-600/30">
        <div className="text-xs font-medium text-orange-300 mb-2">
          🔍 Why this was recommended:
        </div>
        {reasons.strong.length > 0 && (
          <div className="mb-2">
            {reasons.strong.map((reason, idx) => (
              <div
                key={idx}
                className="text-xs text-green-400 flex items-start gap-1"
              >
                <span className="font-bold">✓</span>
                <span>
                  You answered <strong>{reason.optionLabel}</strong> to{" "}
                  {reason.questionName}
                </span>
              </div>
            ))}
          </div>
        )}
        {reasons.optional.length > 0 && (
          <div>
            {reasons.optional.map((reason, idx) => (
              <div
                key={idx}
                className="text-xs text-gray-300 flex items-start gap-1"
              >
                <span>○</span>
                <span>
                  You answered <strong>{reason.optionLabel}</strong> to{" "}
                  {reason.questionName}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className={getCardClasses()}>
      {/* Shiny overlay effect */}
      {isShiny && (
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-200/20 to-transparent animate-shine -skew-x-12 pointer-events-none" />
      )}

      <div className={cn("flex", "items-center", "gap-4 mb-5")}>
        {getIconFromBoostrapIconKey(
          recommendation.iconKey,
          [
            "h-16 w-16",
            "text-primary-color transition-transform duration-200 opacity-80 group-hover:opacity-100",
          ],
          {
            color: recommendation.iconColor || "currentColor",
          },
        )}
        <div>
          <h3 className={cn("font-semibold", "text-lg lg:text-2xl mb-2")}>
            {index + 1}. {recommendation.name}
          </h3>
          {recommendation.shortDescription && (
            <div className={cn("text-sm", "text-description-color mb-2")}>
              {recommendation.shortDescription}
            </div>
          )}
          <div className="text-xs text-green-400 flex items-center gap-1 font-medium">
            <ClockHistory className="h-3 w-3" />
            {timeInfo.text}
          </div>
          {renderAdminReasons()}
        </div>
      </div>

      <div className="xl:flex xl:justify-between w-full">
        <div className="xl:w-1/2 w-full">
          <ul className={cn("mt-2", "pl-6 lg:text-sm 2xl:text-lg")}>
            {recommendation.benefits.map((benefit, benefitIndex) => (
              <li
                key={benefitIndex}
                className={cn(
                  "list-none group-hover:translate-x-[10px]",
                  benefitIndex % 4 === 0 && "duration-150",
                  benefitIndex % 4 === 1 && "duration-500",
                  benefitIndex % 4 === 2 && "duration-700",
                  benefitIndex % 4 === 3 && "duration-1000",
                )}
              >
                <Check className={cn("inline", "text-green-600", "mr-2")} />
                {benefit}
              </li>
            ))}
          </ul>
        </div>

        {(recommendation.primaryAction ||
          recommendation.type === "notify" ||
          recommendation.type === "contact") && (
          <div className="xl:w-1/2 text-right flex flex-col justify-end mt-10 lg:justify-center items-center lg:items-end w-full">
            {renderActions()}
          </div>
        )}
      </div>
    </Card>
  );
}
